'use client';

import { useEffect, useState } from 'react';
import {
  HStack,
  Heading,
  Icon,
  SkeletonText,
  Text,
  VStack,
  chakra,
} from '@chakra-ui/react';
import { MdRefresh } from 'react-icons/md';
import { motion } from 'framer-motion';

import { GiftcardNotFound } from 'components/GiftcardsErrorPage/components/GiftcardNotFound';
import { ZmyleErrorId } from 'utils/constants';
import { IconButton } from 'components/IconButton';
import * as AppLayout from 'components/AppLayout';
import { resolveErrorMessage } from './resolveErrorMessage';
import { Server } from './components/Server';
import { Alert } from './components/Alert';
import { Unauthorized } from './components/Unauthorized';
import { NotFound } from './components/NotFound';
import { Warning } from './components/Warning';
import { FrameContainer } from './components/FrameContainer';

const MotionIconContainer = chakra(motion.span, {
  baseStyle: {
    display: 'flex',
  },
});

interface Props {
  error: ZmyleError;
  reset?(): void;
}

export const GiftcardsErrorPage = (props: Props) => {
  const { error, reset } = props;

  const [message, setMessage] = useState('');

  const { errorId, status } = error;

  useEffect(() => {
    const getMessage = async () => {
      const { message: _message } = await resolveErrorMessage(error);

      setMessage(_message);
    };

    getMessage();
  }, []);

  const getFrame = () => {
    if (errorId === ZmyleErrorId.NO_SUCH_GIFTCARD) return GiftcardNotFound;

    if (status === 400) return Warning;

    if (status === 401 || status === 403) return Unauthorized;

    if (status === 404) return NotFound;

    if (status === 429) return Alert;

    return Server;
  };

  const Frame = getFrame();

  return (
    <AppLayout.Root data-testid="giftcards-error-page">
      <AppLayout.DesktopView />

      <AppLayout.MainView>
        <AppLayout.MainContainer>
          <AppLayout.NetworkLogo />

          <AppLayout.GiftcardAmount />

          <AppLayout.Main>
            <VStack h="full" w="full" spacing="1.5rem">
              <Heading fontSize="4rem">{status}</Heading>

              <FrameContainer>
                <Frame />
              </FrameContainer>

              <HStack w="full" align="center" justify="center" spacing="1rem">
                <SkeletonText
                  isLoaded={Boolean(message)}
                  minW="6.25rem"
                  noOfLines={1}
                >
                  <Text textAlign="center">{message}</Text>
                </SkeletonText>

                {reset && (
                  <IconButton
                    aria-label="reset"
                    shadow="lg"
                    rounded="xl"
                    initial="initial"
                    whileHover="rotate"
                    onClick={reset}
                  >
                    <MotionIconContainer
                      variants={{
                        initial: { rotate: 0 },
                        rotate: { rotate: 360, transition: { duration: 0.3 } },
                      }}
                    >
                      <Icon as={MdRefresh} fontSize="1.5rem" />
                    </MotionIconContainer>
                  </IconButton>
                )}
              </HStack>
            </VStack>
          </AppLayout.Main>
        </AppLayout.MainContainer>
      </AppLayout.MainView>
    </AppLayout.Root>
  );
};
